// Archivo: DashboardInventario.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore'; 
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Modal from '../../ui/Modal';
import StockEntryForm from '../../ui/StockEntryForm';

const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;

export default function DashboardInventario({ db, showModal, currentUser, catalogs, stock, permissions }) {
    const [batchItems, setBatchItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const filteredStockForView = useMemo(() => {
        return stock.filter(item => item.quantity > 0 && (currentUser.role === 'administrador' || currentUser.role === 'supervisor' || item.locationId === currentUser.location));
    }, [stock, currentUser]);
    
    const totalQuantity = useMemo(() => {
        return filteredStockForView.reduce((sum, item) => sum + item.quantity, 0);
    }, [filteredStockForView]);
    
    const batchTotalQuantity = useMemo(() => {
        return batchItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    }, [batchItems]);

    useEffect(() => {
        if (catalogs) setIsLoading(false);
    }, [catalogs]);

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        const logoImg = document.getElementById('company-logo-for-pdf');
        if (logoImg) {
            doc.addImage(logoImg, 'PNG', 14, 12, 50, 15);
        }
        doc.setFontSize(18);
        doc.text("Reporte de Inventario", 14, 35);
        doc.setFontSize(11);
        doc.setTextColor(100);
        const reportDate = `Generado el: ${new Date().toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' })}`;
        doc.text(reportDate, 14, 42);
        const reportUser = `Generado por: ${currentUser.name}`;
        doc.text(reportUser, 14, 49);
        const tableColumns = ["Ubicación", "Producto", "Diseño", "Talla", "Color", "Cantidad"];
        const tableRows = filteredStockForView.map(item => [item.locationName, item.productName, item.designName, item.size, item.color, item.quantity]);
        autoTable(doc, {
            head: [tableColumns],
            body: tableRows,
            startY: 55,
            headStyles: { fillColor: [22, 160, 133] },
        });
        const date = new Date().toISOString().slice(0, 10);
        doc.save(`reporte-inventario-${date}.pdf`);
    };

    const handleAddToBatch = (formData) => {
        const product = catalogs.products.find(p => p.id === formData.productId);
        const location = catalogs.locations.find(l => l.id === formData.locationId);
        const design = formData.designId !== 'NONE' ? catalogs.designs.find(d => d.id === formData.designId) : null;
        const newItem = {
            ...formData,
            tempId: Date.now().toString(36) + Math.random().toString(36).substring(2),
            productName: product?.name || 'N/A',
            designName: design ? `${design.name} (${formData.designSize || ''})` : 'Sin Diseño',
            locationName: location?.name || 'N/A',
        };
        setBatchItems(prev => [...prev, newItem]);
        setIsModalOpen(false);
    };

    const handleUpdateBatchItem = (formData) => {
        const product = catalogs.products.find(p => p.id === formData.productId);
        const location = catalogs.locations.find(l => l.id === formData.locationId);
        const design = formData.designId !== 'NONE' ? catalogs.designs.find(d => d.id === formData.designId) : null;
        const updatedItem = {
            ...formData,
            tempId: editingItem.tempId, 
            productName: product?.name || 'N/A',
            designName: design ? `${design.name} (${formData.designSize || ''})` : 'Sin Diseño',
            locationName: location?.name || 'N/A',
        };
        setBatchItems(prevItems => prevItems.map(item => 
            item.tempId === editingItem.tempId ? updatedItem : item
        ));
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleStartEdit = (itemToEdit) => {
        setEditingItem(itemToEdit);
        setIsModalOpen(true);
    };

    const handleRemoveFromBatch = (tempId) => {
        setBatchItems(prev => prev.filter(item => item.tempId !== tempId));
    };

    const handleCommitBatch = async () => {
        if (batchItems.length === 0) {
            showModal("Vacío", "No hay items en el lote para guardar.");
            return;
        }
        try {
            const batch = writeBatch(db);
            const batchId = `ingreso-${Date.now()}`; 
            for (const item of batchItems) {
                // Objeto explícito para asegurar que solo los datos del formulario se guarden
                const dataToSave = {
                    productId: item.productId,
                    locationId: item.locationId,
                    size: item.size,
                    color: item.color,
                    designId: item.designId,
                    designSize: item.designSize,
                    quantity: parseInt(item.quantity, 10),
                };

                const sku = `${dataToSave.productId}_${dataToSave.designId || 'NONE'}_${dataToSave.size}_${dataToSave.color}_${dataToSave.designSize || 'NONE'}_${dataToSave.locationId}`;
                const existingDoc = stock.find(stockItem => stockItem.sku === sku);

                if (existingDoc) {
                    const docRef = doc(db, 'inventory_stock', existingDoc.id);
                    const newQuantity = existingDoc.quantity + dataToSave.quantity;
                    batch.update(docRef, { quantity: newQuantity });
                } else {
                    const newStockRef = doc(collection(db, 'inventory_stock'));
                    batch.set(newStockRef, { ...dataToSave, sku });
                }

                const entryRef = doc(collection(db, 'inventory_entries'));
                batch.set(entryRef, {
                    ...dataToSave,
                    batchId: batchId,
                    createdBy: currentUser.name,
                    creatorId: currentUser.uid,
                    createdAt: serverTimestamp(),
                    type: 'ingreso',
                });
            }
            await batch.commit();
            showModal("Éxito", `${batchItems.length} ingreso(s) de stock han sido guardados y registrados correctamente.`);
            setBatchItems([]);
        } catch (error) {
            console.error("Error al guardar el lote:", error);
            showModal("Error", `No se pudo guardar el lote de stock: ${error.message}`);
        }
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <img id="company-logo-for-pdf" src="/aralellum.png" style={{ display: 'none' }} alt="Logo de la empresa"/>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-800">Dashboard de Inventario</h3>
                <div className="flex gap-4">
                    <button onClick={handleDownloadPDF} className="btn-secondary bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center"><DownloadIcon /> Descargar PDF</button>
                    {(currentUser.role === 'administrador' || currentUser.role === 'supervisor') && (
                        <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} className="btn-primary"> + Ingresar Stock </button>
                    )}
                </div>
            </div>
            <p className="text-gray-600 mb-6">Aquí puedes ver el inventario actual. Usa el botón "+ Ingresar Stock" para añadir nuevos productos o "Descargar PDF" para generar un reporte.</p>
            {batchItems.length > 0 && (
                <div className="mb-8 p-4 border rounded-lg bg-sky-50">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold text-gray-700">Lote de Ingreso Pendiente</h4>
                        <button onClick={handleCommitBatch} className="btn-success">Guardar Lote en Inventario</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-200"><tr><th className="p-2 text-left text-sm font-semibold text-gray-600">Producto</th><th className="p-2 text-left text-sm font-semibold text-gray-600">Detalles</th><th className="p-2 text-right text-sm font-semibold text-gray-600">Cantidad</th><th className="p-2 text-center text-sm font-semibold text-gray-600">Acciones</th></tr></thead>
                            <tbody>
                                {batchItems.map((item) => (
                                    <tr key={item.tempId} className="border-b">
                                        <td className="p-2"><p className="font-medium">{item.productName}</p><p className="text-xs text-indigo-500">{item.designName}</p></td>
                                        <td className="p-2 text-sm">{item.locationName} / {item.size} / {item.color}</td>
                                        <td className="p-2 text-right font-bold">{item.quantity}</td>
                                        <td className="p-2 text-center">
                                            <div className="flex justify-center items-center space-x-2">
                                                <button onClick={() => handleStartEdit(item)} className="text-sky-600 hover:text-sky-800 p-1" title="Editar ítem">
                                                    <EditIcon />
                                                </button>
                                                <button onClick={() => handleRemoveFromBatch(item.tempId)} className="text-red-500 hover:text-red-700 p-1" title="Quitar del lote">
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-gray-300">
                                    <td colSpan="2" className="p-2 text-right font-semibold text-gray-800">Total del Lote:</td>
                                    <td className="p-2 text-right font-bold text-lg text-gray-800">{batchTotalQuantity}</td>
                                    <td className="p-2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
            {isLoading ? <p>Cargando inventario...</p> : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px]">
                        <thead className="bg-gray-100"><tr><th className="p-3 text-left text-sm font-semibold text-gray-600">Ubicación</th><th className="p-3 text-left text-sm font-semibold text-gray-600">Producto</th><th className="p-3 text-left text-sm font-semibold text-gray-600">Diseño</th><th className="p-3 text-left text-sm font-semibold text-gray-600">Talla</th><th className="p-3 text-left text-sm font-semibold text-gray-600">Color</th><th className="p-3 text-right text-sm font-semibold text-gray-600">Cantidad</th></tr></thead>
                        <tbody>
                            {filteredStockForView.map(item => (
                                <tr key={item.id} className="border-b hover:bg-sky-50"><td className="p-3">{item.locationName}</td><td className="p-3 font-medium">{item.productName}</td><td className="p-3">{item.designName}</td><td className="p-3">{item.size}</td><td className="p-3">{item.color}</td><td className="p-3 font-bold text-right">{item.quantity}</td></tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-200 font-bold text-gray-700">
                                <td colSpan="5" className="p-3 text-right">Total de Productos en Vista:</td>
                                <td className="p-3 text-right">{totalQuantity}</td>
                            </tr>
                        </tfoot>
                    </table>
                    {filteredStockForView.length === 0 && !isLoading && <p className="text-center text-gray-500 py-8">No hay inventario con stock disponible.</p>}
                </div>
            )}
            <Modal isOpen={isModalOpen} onClose={handleCancelModal} title={editingItem ? "Editar Ítem del Lote" : "Añadir a Lote de Ingreso"}>
                <StockEntryForm 
                    products={catalogs?.products || []} 
                    designs={catalogs?.designs || []} 
                    locations={catalogs?.locations || []} 
                    onSave={editingItem ? handleUpdateBatchItem : handleAddToBatch} 
                    onCancel={handleCancelModal}
                    initialData={editingItem}
                />
            </Modal>
        </div>
    );
};